import axios from "axios";
import moment from "moment";
import { SEARCH_BY, EyeTypes, OCTBrowserType } from "../config/config";
import { processScanAndSeriesTypes } from "../imageBrowserContext/contextFactory";
import { defaultSize } from "../canvas/geometry";
import PlaceholderContext from "../imageBrowserContext/PlaceholderContext";
import OverlayBrowserContext from "../imageBrowserContext/overlayBrowserContext";
import { isoDateTimeFormat, formatDateDisplay } from "../helpers/dateUtils";
import Annotation from "../canvas/annotations/annotation";
import { disableAutologoutEvents, setupAutologoutEvents } from "../helpers/idle";
import { logDetailed } from "../helpers/utils";
import { setOCTButtonText } from "../imageBrowserContext/octUtils";

export const initialize = (state, payload) => {
  Object.assign(state, payload);
};

export const setWindowSize = state => {
  document.body.height = window.innerHeight;
  document.body.width = window.innerWidth;
  state.windowHeight = window.innerHeight;
  state.windowWidth = window.innerWidth;
};

export const setCalledFullscreenToggle = state => state.calledFullscreenToggle = true;
export const resetCalledFullscreenToggle = state => state.calledFullscreenToggle = false;

export const resetFullscreen = state => state.isFullscreen = false;
export const toggleFullscreenState = state => state.isFullscreen = !state.isFullscreen;

export const toggleFullscreen = state => {
  var elem = document.documentElement;
  if (
    document.fullscreenEnabled ||
    document.webkitFullscreenEnabled ||
    document.mozFullScreenEnabled ||
    document.msFullscreenEnabled
  ) {
    if (!state.isFullscreen) {
      if (elem.requestFullscreen) {
        elem.requestFullscreen();
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      } else if (elem.mozRequestFullScreen) {
        elem.mozRequestFullScreen();
      } else if (elem.msRequestFullscreen) {
        elem.msRequestFullscreen();
      }
      toggleFullscreenState(state);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      toggleFullscreenState(state);
    }
  }
}

export const setOpenedFromApp = state => {
  state.isOpenedInApp = true;
}

// #region authorization management
export const setAxiosHeader = (state, access) => {
  // use for app print page
  // set up axios hearder for api calls without other auth info
  axios.defaults.headers.common.Authorization = `Bearer ${access}`;
};
export const updateAuth = (state, auth) => {
  // if we update auth from the refresh-token workflow, we don't get the user identifying info back
  // so, assume that, if auth.user is missing, we need to reuse the old auth.user
  if (!auth.user && state.auth && state.auth.user)
    auth.user = state.auth.user;
  state.auth = auth;

  let localState = { auth: state.auth };
  let json = JSON.stringify(localState);
  sessionStorage.setItem("auth", json);

  setAuthHeader(state);
  setRefreshTokenExpiration(state);
};
export const setAuthHeader = state => {
  const accessToken = state.auth && state.auth.access_token;
  if (accessToken)
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
};
export const setRefreshTokenExpiration = (state) => {
  const expires_in = state.auth && state.auth.expires_in;
  if (expires_in) {
    const expires_time = new Date().getTime() + expires_in * 1000;
    state.auth.expires_time = expires_time;
  }
};
export const goActive = state => state.isIdle = false;
export const goInactive = state => state.isIdle = true;
export const disableAutoLogout = state => state.disableAutoLogout = true;
export const enableAutoLogout = state => state.disableAutoLogout = false;
export const setBackgroundTimestamp = (state, time) => state.backgroundTimestamp = time;
export const logout = state => {
  delete axios.defaults.headers.common.Authorization;
  state.auth = null;
  state.wasEmrPro = false;
  state.emrProSearchParams = null;
  setPatients(state, []);
  setExams(state, []);
  clearSelectedPatient(state);
  resetSearch(state);
  disableAutologoutEvents(); // make sure auto logout is disabled at the end of the logout process
  sessionStorage.removeItem("auth");
};
// #endregion

// #region mfa
export const set2faEnabled = (state, enabled) => {
  state.mfa.enabled = enabled;
};
export const set2faSecret = (state, secret) => {
  state.mfa.secret = secret;
};
// #endregion

// #region user preferences
export const setUserPrefs = (state, userPrefs) => {
  state.userPrefs.fullscreen = (typeof userPrefs.fullscreen === "undefined") ? null : userPrefs.fullscreen;
  state.userPrefs.patientDateSetting = (typeof userPrefs.patientTimespan === "undefined") ? null : userPrefs.patientTimespan;
  state.userPrefs.defaultColorTheme = (typeof userPrefs.defaultColorTheme === "undefined") ? null : userPrefs.defaultColorTheme;
  state.userPrefs.pageSize = (typeof userPrefs.pageSize === "undefined") ? null : userPrefs.pageSize;
  state.userPrefs.autoPatientExamRefresh = (typeof userPrefs.autoPatientExamRefresh === "undefined") ? null : userPrefs.autoPatientExamRefresh;
  state.userPrefs.defaultHVF = (typeof userPrefs.defaultHVF === "undefined") ? null : userPrefs.defaultHVF;
  state.userPrefs.defaultCirrusView = (typeof userPrefs.defaultCirrusView === "undefined") ? null : userPrefs.defaultCirrusView;
  state.userPrefs.defaultHVFview = (typeof userPrefs.defaultHVFview === "undefined") ? null : userPrefs.defaultHVFview;
  state.userPrefs.multiviewCollapse = (typeof userPrefs.multiviewCollapse === "undefined") ? null : userPrefs.multiviewCollapse;
  state.userPrefs.cirrusScanDisplayRatio = (typeof userPrefs.cirrusScanDisplayRatio === "undefined") ? null : userPrefs.cirrusScanDisplayRatio;
};

export const setPatientDateSetting = (state, dateSetting) => {
  state.userPrefs.patientDateSetting = dateSetting;
};

export const setAutoPatientRefresh = (state, value) => {
  state.userPrefs.autoPatientExamRefresh = value;
}

// #endregion

// #region snackbar statuses
export const setDelayedStatus = (state, status) => {
  state.delayedStatus = {
    message: status.message,
    level: status.level
  };
};
export const tryDisplayDelayedStatus = state => {
  if (state.delayedStatus) {
    displayStatus(state, state.delayedStatus);
    state.delayedStatus = null;
  }
};
export const displayStatus = (state, status) => {
  state.status.level = status.level;
  state.status.message = status.message;
  state.status.timeout = status.indefinite ? 0 : 5000;
};
export const destroyStatus = state => {
  state.status.message = null;
  state.status.level = null;
};
// #endregion

// #region static data
export const setPhysicians = (state, physicians) => {
  state.physicians = physicians;
};
export const setDevices = (state, devices) => {
  state.devices = devices;
};
export const setDeviceLogs = (state, logs) => {
  state.deviceLogs = logs;
}
export const setdevLocations = (state, devLocations) => {
  state.devLocations = devLocations;
};
export const setProcedures = (state, procedures) => {
  state.procedures = procedures;
};
export const siteInfo = (state, siteInfo) => {
  state.siteInfo = siteInfo;
};
export const setActDirEnabled = (state, isEnabled) => {
  state.isActDirEnabled = isEnabled;
}
export const setPracticeGroupsList = (state, list) => {
  state.practiceGroupsList = list;
}
// #endregion

// #region administration
export const userList = (state, userList) => {
  state.userList = userList;
};
// #endregion

// #region patient search
export const quickSearch = (state, text) => {
  logDetailed("mutations", "quickSearch", text);
  // clear other searches
  clearSearch(state);
  state.patientSearch.searchBy = text ? SEARCH_BY.Keyword : SEARCH_BY.All;
  state.patientSearch.keyword = text;
};
export const openAdvancedSearch = state => {
  logDetailed("mutations", "openAdvancedSearch");
  state.advancedSearchDialog = true;
  state.patientSearch.advancedSearch = null;
};
export const closeAdvancedSearch = state => {
  logDetailed("mutations", "closeAdvancedSearch");
  state.advancedSearchDialog = false;
  state.patientSearch.advancedSearch = null;
};
export const advancedSearch = (state, search) => {
  logDetailed("mutations", "advancedSearch", search);
  // clear other searches
  clearSearch(state);

  state.advancedSearchDialog = false;
  state.patientSearch.searchBy = SEARCH_BY.Advanced;
  let startDate = search.date.from ? moment(search.date.from + "T00:00:00") : null;
  let endDate = search.date.to ? moment(search.date.to + "T00:00:00") : null;
  state.patientSearch.dateStart = startDate;
  state.patientSearch.dateEnd = endDate;
  state.patientSearch.advancedSearch = {
    patientID: search.patientID,
    patientName: search.patientName,
    isTeachingCase: JSON.parse(search.teaching),
    isDictated: JSON.parse(search.dictated),
    examTypes: search.device,
    examPhysicians: search.physicians,
    examPathology: search.examPathology,
    searchAppend: search.searchOP,
    patientDOB: search.dob,

    // todo - are these fields missing from the advanced search dialog?
    ptSrNo: "",
    exSrNo: "",
    examDate: "",
    fileSrNo: "",
    examProcedure: []
  };
};
export const resetSearch = state => {
  logDetailed("mutations", "resetSearch");
  clearSearch(state);
};
export const ssoSearch = (state, query) => {
  logDetailed("mutations", "ssoSearch", query);

  // Look at local storage to see if initial search was an ssoSearch with emrPro
 if (query.emrpro && query.emrpro.toLowerCase() == "true"){
    logDetailed("mutations", "ssoSearch", "emrpro was true in search query");
    state.emrProSearchParams = query;
    state.wasEmrPro = true;
  }
  else{
    state.emrProSearchParams = null;
    state.wasEmrPro = false;
  }

  // clear other searches
  clearSearch(state);

  state.ssoSearch = query;
  state.patientSearch.searchBy = (query.emrpro && query.emrpro.toLowerCase() == "true") ? SEARCH_BY.EMRLink : SEARCH_BY.Advanced;
  state.patientSearch.dateStart = query.exdate ? moment(query.exdate).startOf("day").format(isoDateTimeFormat) : null;
  state.patientSearch.dateEnd = query.exdate ? moment(query.exdate).endOf("day").format(isoDateTimeFormat) : null;
  let names = [query.ptlname, query.ptfname].filter(n => !!n);
  let patientName = names.join(", ").replace(new RegExp("\\+", "g"), " ");// replace + with space because whitespace is ignored and replace with + by base64 encoding
  let devices = query.extype ? state.devices.filter(d => d.description.includes(query.extype)) : [];
  let searchAppend = (query.exsrno || query.ptid || query.ptlname || query.ptfname || query.exdate || query.extype) ? "and" : "or";
  state.patientSearch.advancedSearch = {
    patientID: query.ptid || "",
    patientName: patientName || "",
    patientDOB: query.ptdob,
    examTypes: devices,
    exSrNo: query.exsrno || "",
    ptSex: query.ptsex || "",
    searchAppend: searchAppend,

    examDate: null,
    isTeachingCase: null,
    isDictated: null,
    examPhysicians: [],
    examPathology: "",
    ptSrNo: "",
    fileSrNo: "",
    examProcedure: []
  };
};
export const clearSSOSearch = state => {
  logDetailed("mutations", "clearSSOSearch");
  state.ssoSearch = null;
}
export const setSearchTimespan = (state, timespan) => {
  logDetailed("mutations", "setSearchTimespan", "timespan", timespan, "state.patientSearch.keyword", state.patientSearch.keyword);

  // clear quicksearch, advancedsearch when setting search timespan
  if (!state.ssoSearch)
    clearSearch(state);
  doSetSearchTimespan(state, timespan);
};
function clearSearch(state) {
  logDetailed("mutations", "clearSearch");
  state.patientSearch.keyword = "";
  state.patientSearchOptions.page = 1;
  doSetSearchTimespan(state, state.userPrefs.patientDateSetting || "all");
  state.patientSearch.advancedSearch = null;
  state.ssoSearch = null;
}
function doSetSearchTimespan(state, timespan) {
  logDetailed("mutations", "doSetSearchTimespan", timespan);
  state.patientSearch.timespan = timespan;
  if (!state.ssoSearch) {
    if (timespan && timespan != "all") {
      let { startDate, endDate } = getDateInterval(timespan);
      state.patientSearch.searchBy = SEARCH_BY.DateIntervals;
      state.patientSearch.dateStart = startDate;
      state.patientSearch.dateEnd = endDate;
    } else {
      state.patientSearch.searchBy = SEARCH_BY.All;
      state.patientSearch.dateStart = null;
      state.patientSearch.dateEnd = null;
    }
  }
}
function getDateInterval(interval) {
  return {
    startDate: moment().startOf(interval).format(isoDateTimeFormat),
    endDate: moment().endOf(interval).format(isoDateTimeFormat)
  };
}
export const updatePatientSearchOptions = (state, options) => {
  logDetailed("mutations", "updatePatientSearchOptions", options);
  state.patientSearchOptions = { ...options };
}
// #endregion

// #region patients, exams, and images
export const startLoadingPatients = state => state.loadingPatients = true;
export const stopLoadingPatients = state => state.loadingPatients = false;
export const setPatients = (state, patients) => {
  state.patients = patients;
};
export const setTotalPatients = (state, total) => state.totalPatients = total;
export const selectPatient = (state, patient) => state.patient = patient;
export const clearSelectedPatient = state => {
  state.patient = null;
  state.exams = null;
  state.selectedExams = [];
  clearBrowserContext(state);
};
export const startLoadingExams = state => state.loadingExams = true;
export const stopLoadingExams = state => state.loadingExams = false;
export const setExams = (state, exams) => {
  state.exams = exams;
  // if there are any exams, update selected exams to the intersection of the previous selected exams and the new exams
  if (exams.length)
    state.selectedExams = exams.filter(e => state.selectedExams.some(se => se.exSrNo == e.exSrNo));
};
export const selectExams = (state, exams) => {
  state.selectedExams = exams || [];
};
export const setExamToDelete = (state, exam) => state.examToDelete = exam;
export const addExamToDeleteList = (state, exams) => {
    state.examsToDelete = exams;
}
export const clearExamDeleteList = (state) => state.examsToDelete = [];
export const setExamToDownload = (state, link) => state.examToDownload = link;
export const setExamToEdit = (state, exam) => state.examToEdit = exam;
export const clearEditExam = state => state.examToEdit = null;
export const clearImageOrder = state => state.imageOrder = [];
export const setExamImageMetadataLoaded = (state, exSrNo) =>
{
  let exams = state.exams.filter(e => e.exSrNo == exSrNo);
  if (exams && exams.length)
    exams[0].loadedImageMetadata = true;
}
export const storeImages = (state, { exImgDataRes, exDevice, exDate, dateDisplay, moment: exMoment }) => {
  let images = exImgDataRes.exImgData;
  let exSrNo = exImgDataRes.exSrNo;
  
  if (!images || !images.length) return;

  for (let i = 0; i < images.length; i++) {
    let image = images[i];
    let order = {
      flSrNo: image.flSrNo,
      exSrNo: exSrNo,
      imgSubType: image.imgSubType,
      imgEye: image.imgEye
    };
    
    if (!state.imageOrder.some(e => e.flSrNo == order.flSrNo))
      state.imageOrder.push(order);

    let imgKey = image.flSrNo + "";
    if (image.imgSubType) imgKey += "_" + image.imgSubType;
    if (!state.imageMap.some(i => i.imgKey === imgKey)) {
      image.exDevice = exDevice;
      image.exDate = exDate;
      image.exMoment = exMoment;
      image.exDateDisplay = dateDisplay;
      image.exIndex = i + 1;
      image.imgKey = imgKey;
      image.exSrNo = exSrNo;
      image.isSelected = false;

      addImgWrapper(image, "img");
      addImgWrapper(image, "thumb");
      addImgWrapper(image, "proof");

      state.imageMap.push(image);
    }
  }
};
function addImgWrapper(image, prefix) {
  image[prefix + "NotFound"] = false;
  image[prefix + "IsLoading"] = false;
  image[prefix + "IsQueued"] = false;
  image[prefix + "HtmlObject"] = null;
  image[prefix + "ObjectUrl"] = null;
  image[prefix + "Size"] = defaultSize();
}
export const setImageState = (state, imageState) => {
  let image = state.imageMap.find(i => i.imgKey === imageState.imgKey);
  if (!image) return;
  Object.assign(image, imageState);
};
export const removeImagesImageMap = (state, imgSrNos) => {
  state.imageMap = state.imageMap.filter(i => !imgSrNos.includes(i.flSrNo));
}
export const updateOCTExamInfo = (state, data) => {
  if (!data || !data.length) return;
  data.forEach(d => {
    let exam = state.exams.find(e => e.exSrNo === d.exSrNo);
    if (!exam) return;
    exam.odGroups = processScanAndSeriesTypes(exam.manufacturer, d.odGroupList, exam.moment, EyeTypes.OD);
    exam.osGroups = processScanAndSeriesTypes(exam.manufacturer, d.osGroupList, exam.moment, EyeTypes.OS);
    if (exam.odGroups.length)
      exam.browserType = setBrowserType(exam.odGroups);
    if (exam.osGroups.length && !exam.browserType)
      exam.browserType = setBrowserType(exam.osGroups);
    if (!exam.browserType)
      exam.octDateButtonText = exam.dateDisplay;
    else {
      setOCTButtonText(exam);
    }
  });
};

export const updateExamImageDate = (state, exam) => {
  let images = state.imageMap.filter(i => i.exSrNo === exam.exSrNo);
  images.forEach(e => {
    e.exDate = exam.dateIso;
    e.exDateDisplay = formatDateDisplay(exam.dateIso);
  });
}

export const updateMultiviewEye = (state, eye) => {
  state.imageBrowserContext && state.imageBrowserContext.switchEye
    && state.imageBrowserContext.switchEye(eye);
}
// #endregion

// #region image browser
export const setBrowserContext = (state, context) => {
  state.imageBrowserContext = context;
};
export const clearBrowserContext = state => {
  state.imageBrowserContext && state.imageBrowserContext.clearContext();
  state.imageBrowserContext = null;
};
export const setBrowserType = (groups) => {
  if (groups.some(item => item.browserType == OCTBrowserType.Cirrus) && groups.some(item => item.browserType == OCTBrowserType.Spectralis))
    return "Cirrus | Spectralis";
  else {
    if (groups.some(item => item.browserType == OCTBrowserType.Cirrus))
      return "Cirrus";
    else if (groups.some(item => item.browserType == OCTBrowserType.Spectralis))
      return "Spectralis";
  }
  return "";
};
// #region select images
export const selectImage = (state, image) => {
  state.imageBrowserContext && state.imageBrowserContext.selectImage
    && state.imageBrowserContext.selectImage(image);
};
export const resetImage = state => {
  state.imageBrowserContext && selectImage(state, state.imageBrowserContext.image);
};
export const setCurrentImageLoading = (state, isLoading) => {
  state.imageBrowserContext && state.imageBrowserContext.image
    && (state.imageBrowserContext.image.imgIsLoading = isLoading);
};
export const setPlayInterval = (state, intervalId) => {
  state.imageBrowserContext && state.imageBrowserContext.setPlayInterval
    && state.imageBrowserContext.setPlayInterval(intervalId);
};
export const pause = state => {
  state.imageBrowserContext && state.imageBrowserContext.pause
    && state.imageBrowserContext.pause();
};
export const setPlayRate = (state, rate) => {
  state.imageBrowserContext && state.imageBrowserContext.setPlayRate
    && state.imageBrowserContext.setPlayRate(rate);
};
export const setDiagnosisText = (state, text) => {
  state.imageBrowserContext && state.imageBrowserContext.setDiagnosisText
    && state.imageBrowserContext.setDiagnosisText(text);
}
export const setDiagnosisColor = (state, color) => {
  state.imageBrowserContext && state.imageBrowserContext.setDiagnosisColor
    && state.imageBrowserContext.setDiagnosisColor(color);
}
export const setImageMask = (state, mask) => {
  state.imageBrowserContext && state.imageBrowserContext.setImageMask
    && state.imageBrowserContext.setImageMask(mask);
}
export const toggleDeepDeeHighlight = state => {
  state.imageBrowserContext && state.imageBrowserContext.toggleDeepDeeHighlight
    && state.imageBrowserContext.toggleDeepDeeHighlight();
}
export const setDeepDeeAnnotation = (state, annotation) => {
  state.imageBrowserContext && state.imageBrowserContext.setDeepDeeAnnotation
    && state.imageBrowserContext.setDeepDeeAnnotation(annotation);
}
export const toggleDeepDeeAnnotation = state => {
  state.imageBrowserContext && state.imageBrowserContext.toggleDeepDeeAnnotation
    && state.imageBrowserContext.toggleDeepDeeAnnotation();
}

// #endregion

// #region view sizes and transformations
export const toggleFlipped = state => {
  if (state.imageBrowserContext && state.imageBrowserContext.toggleFlipped)
    state.imageBrowserContext.toggleFlipped();
};
export const toggleFlipped90 = state => {
  if (state.imageBrowserContext && state.imageBrowserContext.toggleFlipped90)
    state.imageBrowserContext.toggleFlipped90();
};
export const setTransform = (state, transform) => {
  state.imageBrowserContext.setTransform(transform);
  state.imageBrowserContext.annotations.forEach(e => {
    e.setParentTransform(transform);
  });
};
// #endregion

// #region color correction
export const flattenedImageReady = (state, ready) => {
  if (state.imageBrowserContext && state.imageBrowserContext.setFlattenedImageReady)
    state.imageBrowserContext.setFlattenedImageReady(ready);
};
export const toggleGrayscaleMode = (state, mode) => {
  if (state.imageBrowserContext && state.imageBrowserContext.toggleGrayscaleMode)
    state.imageBrowserContext.toggleGrayscaleMode(mode);
};
export const clearGrayscaleMode = state => {
  if (state.imageBrowserContext && state.imageBrowserContext.clearGrayscaleMode)
    state.imageBrowserContext.clearGrayscaleMode();
};
export const updateBrightness = (state, brightness) => {
  if (state.imageBrowserContext && state.imageBrowserContext.updateBrightness)
    state.imageBrowserContext.updateBrightness(brightness);
};
export const updateContrast = (state, contrast) => {
  if (state.imageBrowserContext && state.imageBrowserContext.updateContrast)
    state.imageBrowserContext.updateContrast(contrast);
};
export const updateGamma = (state, gamma) => {
  if (state.imageBrowserContext && state.imageBrowserContext.updateGamma)
    state.imageBrowserContext.updateGamma(gamma);
};
export const updateGreenBrightness = (state, brightness) => {
  if (state.imageBrowserContext && state.imageBrowserContext.updateGreenBrightness)
    state.imageBrowserContext.updateGreenBrightness(brightness);
};
export const updateGreenContrast = (state, contrast) => {
  if (state.imageBrowserContext && state.imageBrowserContext.updateGreenContrast)
    state.imageBrowserContext.updateGreenContrast(contrast);
};
// #endregion

// #region annotations and measurements
export const selectCalibration = (state, name) => {
  state.imageBrowserContext && state.imageBrowserContext.selectCalibration(name);
};
export const toggleIsAnnotating = state => {
  if (state.imageBrowserContext && state.imageBrowserContext.toggleIsAnnotating)
    state.imageBrowserContext.toggleIsAnnotating();
};
export const clearAnnotationMode = state => {
  if (state.imageBrowserContext && state.imageBrowserContext.clearAnnotationMode)
    state.imageBrowserContext.clearAnnotationMode();
};
export const updateAnnotationColor = (state, color) => {
  if (state.imageBrowserContext && state.imageBrowserContext.updateAnnotationColor)
    state.imageBrowserContext.updateAnnotationColor(color);
};
export const updateAnnotationTextSize = (state, size) =>
  state.imageBrowserContext && state.imageBrowserContext.updateAnnotationTextSize && state.imageBrowserContext.updateAnnotationTextSize(size);
export const toggleAnnotationMode = (state, mode) => {
  if (state.imageBrowserContext && state.imageBrowserContext.toggleAnnotationMode)
    state.imageBrowserContext.toggleAnnotationMode(mode);
};
export const clearActiveAnnotation = state => {
  if (state.imageBrowserContext && state.imageBrowserContext.clearActiveAnnotation)
    state.imageBrowserContext.clearActiveAnnotation();
};
export const setActiveAnnotation = (state, annotation) => {
  if (state.imageBrowserContext && state.imageBrowserContext.setActiveAnnotation)
    state.imageBrowserContext.setActiveAnnotation(annotation);
};
export const clearAnnotations = state => {
  if (state.imageBrowserContext && state.imageBrowserContext.clearAnnotations)
    state.imageBrowserContext.clearAnnotations();
};
export const addAnnotation = (state, annotation) => {
  if (state.imageBrowserContext && state.imageBrowserContext.addAnnotation)
    state.imageBrowserContext.addAnnotation(annotation);
};
export const deleteAnnotation = (state, index) => {
  if (state.imageBrowserContext && state.imageBrowserContext.deleteAnnotation)
    state.imageBrowserContext.deleteAnnotation(index);
};
export const setTextAnnotationToEdit = (state, annotation) => {
  state.textAnnotationToEdit = annotation;
};
export const setAnnotationText = (state, text) => {
  if (state.textAnnotationToEdit && state.textAnnotationToEdit.updateTextArea)
    state.textAnnotationToEdit.updateTextArea(text);
};
export const updateMagnifier = (state, position) =>
  state.imageBrowserContext && state.imageBrowserContext.updateMagnifier && state.imageBrowserContext.updateMagnifier(position);
export const mousedownMagnifier = (state) =>
  state.imageBrowserContext && state.imageBrowserContext.mousedownMagnifier && state.imageBrowserContext.mousedownMagnifier();
export const mouseupMagnifier = (state) =>
  state.imageBrowserContext && state.imageBrowserContext.mouseupMagnifier && state.imageBrowserContext.mouseupMagnifier();

export const setShapeOutlineContext = (state, context) => {
  state.shapeOutlineContext = context;
}
export const setShapeOutlineSyncContext = (state, context) => {
  state.shapeOutlineSyncContext = context;
}

export const setShapeFillContext = (state, context) => {
  state.shapeFillContext = context;
}
export const setShapeFillSyncContext = (state, context) => {
  state.shapeFillSyncContext = context;
}
// #endregion

// #region rgb
export const toggleRGB = state => {
  if (state.imageBrowserContext && state.imageBrowserContext.toggleRGB)
    state.imageBrowserContext.toggleRGB();
};
export const toggleRGBSync = state => {
  if (state.imageBrowserContext && state.imageBrowserContext.toggleRGBSync)
    state.imageBrowserContext.toggleRGBSync();
};
export const toggleGreenSync = state => {
  if (state.imageBrowserContext && state.imageBrowserContext.toggleGreenSync)
    state.imageBrowserContext.toggleGreenSync();
};
export const setRGBExpanded = (state, expanded) => {
  if (state.imageBrowserContext && state.imageBrowserContext.setRGBExpanded)
    state.imageBrowserContext.setRGBExpanded(expanded);
};
export const selectRGBView = (state, index) => {
  if (state.imageBrowserContext && state.imageBrowserContext.selectRGBView)
    state.imageBrowserContext.selectRGBView(index);
};
export const replicateCurrentRGBToMain = state => {
  if (state.imageBrowserContext && state.imageBrowserContext.replicateCurrentRGBToMain)
    state.imageBrowserContext.replicateCurrentRGBToMain();
};
export const replicateMainToCurrentRGB = state => {
  if (state.imageBrowserContext && state.imageBrowserContext.replicateMainToCurrentRGB)
    state.imageBrowserContext.replicateMainToCurrentRGB();
};
export const replicateMainToAllRGB = state => {
  if (state.imageBrowserContext && state.imageBrowserContext.replicateMainToAllRGB)
    state.imageBrowserContext.replicateMainToAllRGB();
};
export const replicateGreenMainToAllRGB = state => {
  if (state.imageBrowserContext && state.imageBrowserContext.replicateGreenMainToAllRGB)
    state.imageBrowserContext.replicateGreenMainToAllRGB();
};
// #endregion

// #region OCT

// #region oct images
export const storeOCTImages = (state, {
  info,
  images,
  context,
  loadPreviews,
  imageType,
  segmentationImageType
}) => {
  if (!images || !images.length) return;
  for (let i = 0; i < images.length; i++) {
    let image = images[i];
    image.imgKey = context.exSrNo + "_" + context.groupName + "_" + imageType;
    if (image.index !== undefined) image.imgKey += "_" + image.index;
    if (image.originalIndex !== undefined) image.imgKey += "_" + image.originalIndex;
    if (!state.octImageMap.some(i => i.imgKey === image.imgKey)) {
      image.exSrNo = context.exSrNo;
      image.ptSrNo = context.ptSrNo;
      image.groupName = context.groupName;
      image.browserType = context.browserType;
      image.imageType = imageType;

      addImgWrapper(image, "img");

      if (loadPreviews) {
        image.loadPreview = true;
        addImgWrapper(image, "preview");
      }

      if (info && info.sliceHasSegmentation && segmentationImageType) {
        image.loadSegmentation = true;
        image.segmentationIsQueued = true;
        image.segmentationImageType = segmentationImageType;
        image.sliceSegmentationIsLoading = false;
        image.sliceSegmentationLayerData = null;
        image.sliceSegmentationThicknessData = null;
        image.segmentationNotFound = false;
      }

      state.octImageMap.push(image);
    }
  }
};
export const setOCTImageState = (state, imageState) => {
  let image = state.octImageMap.find(i => i.imgKey === imageState.imgKey);
  if (!image) return;
  Object.assign(image, imageState);
};
export const selectOCTImage = (state, { currentKey, imgKey }) => {
  let image = state.octImageMap.find(i => i.imgKey === imgKey);
  if (!image || !state.imageBrowserContext) return;
  state.imageBrowserContext.setOCTImage(currentKey, image);
};
export const loadThicknessMap = (state, imgKey) => {
  if (!state.imageBrowserContext || !state.imageBrowserContext.loadThicknessMap) return;
  let image = state.octImageMap.find(i => i.imgKey === imgKey);
  if (!image) return;
  state.imageBrowserContext.loadThicknessMap(image);
};
export const calculateDifferenceMap = state =>
  state.imageBrowserContext && state.imageBrowserContext.calculateDifferenceMap
  && state.imageBrowserContext.calculateDifferenceMap();
export const calculateDifferenceAnalysis = state =>
  state.imageBrowserContext && state.imageBrowserContext.calculateDifferenceAnalysis
  && state.imageBrowserContext.calculateDifferenceAnalysis();
export const calculateThicknessDifference = state =>
  state.imageBrowserContext && state.imageBrowserContext.calculateThicknessDifference
  && state.imageBrowserContext.calculateThicknessDifference();
export const isBothMeasuring = state =>
  state.imageBrowserContext && state.imageBrowserContext.isBothMeasuring
  && state.imageBrowserContext.isBothMeasuring();
export const reloadChildrenContext = state => 
  state.imageBrowserContext && state.imageBrowserContext.reloadChildrenContext
  && state.imageBrowserContext.reloadChildrenContext();
// #endregion

// #region oct color correction
export const toggleInverted = state =>
  state.imageBrowserContext && state.imageBrowserContext.toggleInverted
  && state.imageBrowserContext.toggleInverted();
export const toggleColorPalette = state =>
  state.imageBrowserContext && state.imageBrowserContext.toggleColorPalette
  && state.imageBrowserContext.toggleColorPalette();
export const toggleSegmentation = state =>
  state.imageBrowserContext && state.imageBrowserContext.toggleSegmentation
  && state.imageBrowserContext.toggleSegmentation();
export const toggleMeasuring = state =>
  state.imageBrowserContext && state.imageBrowserContext.toggleMeasuring
  && state.imageBrowserContext.toggleMeasuring();
export const setMeasurementIndex = (state, index) => {
  state.imageBrowserContext && state.imageBrowserContext.setMeasurementIndex
    && state.imageBrowserContext.setMeasurementIndex(index);
};
export const toggleShowThicknessMap = state =>
  state.imageBrowserContext && state.imageBrowserContext.toggleShowThicknessMap
  && state.imageBrowserContext.toggleShowThicknessMap();
// #endregion

// #region cirrus settings
export const toggleHD = state =>
  state.imageBrowserContext && state.imageBrowserContext.toggleHD
  && state.imageBrowserContext.toggleHD();
export const centerFovea = state =>
  state.imageBrowserContext && state.imageBrowserContext.centerFovea
  && state.imageBrowserContext.centerFovea();
export const toggleETDRS = state =>
  state.imageBrowserContext && state.imageBrowserContext.toggleETDRS
  && state.imageBrowserContext.toggleETDRS();
export const updateThicknessMapOpacity = (state, opacity) =>
  state.imageBrowserContext && state.imageBrowserContext.updateThicknessMapOpacity
  && state.imageBrowserContext.updateThicknessMapOpacity(opacity);
// #endregion

// #region oct multiview
export const toggleLocked = state =>
  state.imageBrowserContext && state.imageBrowserContext.toggleLocked
  && state.imageBrowserContext.toggleLocked();
export const toggleSegmentationCompare = (state, view) =>
  state.imageBrowserContext && state.imageBrowserContext.toggleSegmentationCompare
  && state.imageBrowserContext.toggleSegmentationCompare(view);
export const toggleMeasuringCompare = (state, view) =>
  state.imageBrowserContext && state.imageBrowserContext.toggleMeasuringCompare
  && state.imageBrowserContext.toggleMeasuringCompare(view);
export const setMeasurementIndexCompare = (state, { x, view }) =>
  state.imageBrowserContext && state.imageBrowserContext.setMeasurementIndexCompare
  && state.imageBrowserContext.setMeasurementIndexCompare(x, view);
export const toggleColorCompare = (state, view) =>
  state.imageBrowserContext && state.imageBrowserContext.toggleColorCompare
  && state.imageBrowserContext.toggleColorCompare(view);
export const toggleInvertedCompare = (state, view) =>
  state.imageBrowserContext && state.imageBrowserContext.toggleInvertedCompare
  && state.imageBrowserContext.toggleInvertedCompare(view);
export const pauseCompare = (state, view) =>
  state.imageBrowserContext && state.imageBrowserContext.pauseCompare
  && state.imageBrowserContext.pauseCompare(view);
export const setPlayRateCompare = (state, { view, rate }) =>
  state.imageBrowserContext && state.imageBrowserContext.setPlayRateCompare
  && state.imageBrowserContext.setPlayRateCompare(view, rate);
export const setPlayIntervalCompare = (state, { view, intervalId }) =>
  state.imageBrowserContext && state.imageBrowserContext.setPlayIntervalCompare
  && state.imageBrowserContext.setPlayIntervalCompare(view, intervalId);
export const toggleOverlayCompare = (state, view) =>
  state.imageBrowserContext && state.imageBrowserContext.toggleOverlayCompare
  && state.imageBrowserContext.toggleOverlayCompare(view);
export const updateOverlayOpacityCompare = (state, { view, opacity }) =>
  state.imageBrowserContext && state.imageBrowserContext.updateOverlayOpacityCompare
  && state.imageBrowserContext.updateOverlayOpacityCompare(view, opacity);
export const toggleETDRSCompare = (state, view) =>
  state.imageBrowserContext && state.imageBrowserContext.toggleETDRSCompare
  && state.imageBrowserContext.toggleETDRSCompare(view);
export const updateFoveaAlignment = (state, val) =>
  state.imageBrowserContext && state.imageBrowserContext.updateFoveaAlignment
  && state.imageBrowserContext.updateFoveaAlignment(val);
export const updateScanTypeFilter = (state, val) => 
  state.imageBrowserContext && state.imageBrowserContext.updateScanTypeFilter
  && state.imageBrowserContext.updateScanTypeFilter(val);
// #endregion

// #endregion

// #region hvf
export const selectHVFExam = (state, { exam, index }) =>
  state.imageBrowserContext && state.imageBrowserContext.selectHVFExam(exam, index);
export const selectHVFEye = (state, eye) =>
  state.imageBrowserContext && state.imageBrowserContext.selectHVFEye(eye);
export const previousHVFImage = (state, index) =>
  state.imageBrowserContext && state.imageBrowserContext.previousHVFImage(index);
export const nextHVFImage = (state, index) =>
  state.imageBrowserContext && state.imageBrowserContext.nextHVFImage(index);
// #endregion

// #endregion

// #region loading queues
export const clearImageQueue = (state, queueKey) => {
  if (state[queueKey]) {
    state[queueKey].cancelled = true;
    state[queueKey].queue = null;
    state[queueKey] = null;
  }
};
export const setImageQueue = (state, { queue, queueKey }) => {
  state[queueKey] = queue;
};
export const purgeOldImages = (state, exceptImages) => {
  // Clear images from the normal image map, where they aren't for the currently open set of images
  const ieMax = 50, max = 250;
  let threshold = state.isIE ? ieMax : max;
  let images = state.imageMap.filter(image => image.imgObjectUrl);
  let imagesToPurge = images.filter(image => !exceptImages.includes(image));
  if ((images.length > threshold) && imagesToPurge.length) {
    imagesToPurge.forEach(clearImage);
  }
};
export const purgeOldOCTImages = (state, exceptImages) => {
  let previews = state.octImageMap.filter(image => image.previewObjectUrl);
  previews.forEach(image => clearImage(image, "preview"));

  // Clear images from the octImageMap, where they aren't for the current exam or multiview group
  const ieMaxOCT = 400, maxOCT = 2000;
  let threshold = state.isIE ? ieMaxOCT : maxOCT;
  let images = state.octImageMap.filter(image => image.imgObjectUrl);
  let imagesToPurge = images.filter(image => !exceptImages.includes(image));

  if ((images.length > threshold) && imagesToPurge.length) {
    imagesToPurge.forEach(clearImage);
  }
};
function clearImage(image, prefix = "img") {
  const objUrlKey = prefix + "ObjectUrl", objKey = prefix + "HtmlObject";
  URL.revokeObjectURL(image[objUrlKey]);
  image[objUrlKey] = null;
  image[objKey] && (image[objKey].src = "");
  image[objKey] = null;
}
// #endregion

// #region patient exam multiview browser
export const setPatientExamMultiviewContext = (state, context) =>
  state.patientExamMultiviewContext = context;
export const clearPatientExamMultiviewContext = state =>
  state.patientExamMultiviewContext = null;
export const selectEyeMultiview = (state, eye) =>
  state.patientExamMultiviewContext && state.patientExamMultiviewContext.selectEye(eye);
export const selectDevicesMultiview = (state, devices) =>
  state.patientExamMultiviewContext && state.patientExamMultiviewContext.selectDevices(devices);
export const selectAllDevicesMultiview = state =>
  state.patientExamMultiviewContext && state.patientExamMultiviewContext.selectAllDevices();
export const selectDatesMultiview = (state, dates) =>
  state.patientExamMultiviewContext && state.patientExamMultiviewContext.selectDates(dates);
export const selectAllDatesMultiview = state =>
  state.patientExamMultiviewContext && state.patientExamMultiviewContext.selectAllDates();
export const selectExamsMultiview = (state, exams) =>
  state.patientExamMultiviewContext && state.patientExamMultiviewContext.selectExams(exams);
export const selectAllExamsMultiview = state =>
  state.patientExamMultiviewContext && state.patientExamMultiviewContext.selectAllExams();
export const selectImageMultiview = (state, image) =>
  state.patientExamMultiviewContext && state.patientExamMultiviewContext.selectImage(image);
export const refreshMultiview = (state, exams) =>
  state.patientExamMultiviewContext && state.patientExamMultiviewContext.refreshExams(exams);
export const removeImagesMultiview = (state, imgSrNos) =>
  state.patientExamMultiviewContext && state.patientExamMultiviewContext.removeImages(imgSrNos);
export const setCurrentExamsMultiview = (state, bounds) =>
  state.patientExamMultiviewContext && state.patientExamMultiviewContext.setCurrentExams(bounds);
export const resetMultiviewImages = (state, data) =>
  state.patientExamMultiviewContext && state.patientExamMultiviewContext.resetImages(data);
export const toggleMultiviewCollapse = state =>
  state.patientExamMultiviewContext && state.patientExamMultiviewContext.toggleCollapse();
// #endregion

// #region proofsheet
export const setProofsheetContext = (state, context) => {
  state.proofsheetContext = context;
};
export const clearProofsheetContext = state => {
  state.proofsheetContext = null;
};
export const selectFilterProofsheet = (state, option) =>
  state.proofsheetContext && state.proofsheetContext.setFilterOption(option);
export const selectEyeProofsheet = (state, eye) =>
  state.proofsheetContext && state.proofsheetContext.setEyeFilter(eye);
export const selectImageProofsheet = (state, image) =>
  state.proofsheetContext && state.proofsheetContext.selectImage(image);
export const selectAllProofsheet = state =>
  state.proofsheetContext && state.proofsheetContext.selectAllImages();
export const startDeleteImages = state =>
  state.proofsheetContext && state.proofsheetContext.startDeleteImages();
export const cancelDeleteImages = state =>
  state.proofsheetContext && state.proofsheetContext.cancelDeleteImages();
export const removeImagesProofsheet = (state, imgSrNos) =>
  state.proofsheetContext && state.proofsheetContext.removeImages(imgSrNos);
export const setActiveDownload = (state, { link, text }) => {
  state.activeDownload.link = link;
  state.activeDownload.text = text;
};
export const cancelActiveDownload = state => {
  // todo - make a download files section
  state.activeDownload.link = null;
  state.activeDownload.text = null;
};
export const startMarkImages = state =>
  state.proofsheetContext && state.proofsheetContext.startMarkImages();
export const cancelMarkImages = state =>
  state.proofsheetContext && state.proofsheetContext.cancelMarkImages();
export const updateImageLaterality = (state, { imgSrNos, eye }) => {
  state.imageMap.filter(i => imgSrNos.includes(i.flSrNo))
    .forEach(i => i.imgEye = eye);
};
export const setCurrentImagesProofsheet = (state, bounds) =>
  state.proofsheetContext && state.proofsheetContext.setCurrentImages(bounds);
export const clearSelectedImages = state =>
  state.proofsheetContext && state.proofsheetContext.clearSelectedImages();
// #endregion

// #region placeholder context for exam multiview buttons
export const setPlaceholderContext = state => {
  state.placeholderContext = new PlaceholderContext();
};
export const clearPlaceholderContext = state => {
  state.placeholderContext = null;
};
// #endregion

// #region overlay browser
export const openOverlay = (state, images) => {
  state.overlayContext = new OverlayBrowserContext(images);
}
export const clearOverlay = state => state.overlayContext = null;
export const setLoadingMessage = (state, message) => {
  state.loadingMessage = message;
  if (message) disableAutologoutEvents();
  else setupAutologoutEvents();
}
export const storeI2kImages = (state, images) => {
  if (!images || !images.length) return;
  for (let i = 0; i < images.length; i++) {
    let image = images[i];
    if (!state.i2kImages.some(i => i.imgKey === image.imgKey)) {
      image.imgNotFound = false;
      image.imgIsQueued = false;
      image.imgIsLoading = false;
      image.imgHtmlObject = null;
      image.imgObjectUrl = null;
      image.imgSize = defaultSize();

      state.i2kImages.push(image);
    }
  }
};
export const setI2kImageState = (state, imageState) => {
  let image = state.i2kImages.find(i => i.imgKey === imageState.imgKey);
  if (!image) return;
  Object.assign(image, imageState);
};
export const setOriginalOverlaySettings = (state, settings) => state.originalOverlaySettings = settings;
// #endregion

// #region stereo browser
export const setStereoContext = (state, context) => {
  state.stereoContext = context;
};
export const clearStereoContext = state => {
  state.stereoContext = null;
};
export const setStereoPair = (state, imagePair) => {
  state.stereoContext.selectedPair = imagePair;
};
export const setStereoImagePairList = (state, pairs) => {
  state.stereoContext.imagePairs = pairs;
};
// #endregion

// #region print preview browser
export const setPrintPreviewContext = (state, context) => {
  state.printPreviewContext = context;
};
export const clearPrintPreviewContext = state => {
  state.printPreviewContext = null;
};
export const togglePrintPreviewHeaders = state => {
  state.printPreviewContext.displayHeader = !state.printPreviewContext.displayHeader;
};
export const setPrintPreviewImagesPerPage = (state, num) => {
  state.printPreviewContext.imagesPerSheet = num;
};
export const setPrintPreviewPageLayout = (state, layout) => {
  state.printPreviewContext.pageLayout = layout;
};
export const incrementPrintPreviewCurrentPage = state => {
  state.printPreviewContext.currentPage++;
};
export const decrementPrintPreviewCurrentPage = state => {
  state.printPreviewContext.currentPage--;
};
export const startPrinting = state => {
  state.printPreviewContext.isPrinting = true;
}
export const stopPrinting = state => {
  state.printPreviewContext.isPrinting = false;
}
export const togglePrintPreviewIsPrinting = (state) => {
  state.printPreviewContext.isPrinting = !state.printPreviewContext.isPrinting;
};

export const startLoadingAppPrintImages = state => {
  state.isLoadingAppPrintImages = true;
}
export const finishedLoadingAppPrintImages = state => {
  state.isLoadingAppPrintImages = false;
}

export const setPrintPreviewSheetNum = (state, num) => {
  state.printPreviewContext.sheetNum = num;
  if (state.printPreviewContext.currentPage > num)
    state.printPreviewContext.currentPage = num;
};
export const setPrintPreviewImageNum = (state, num) => {
  state.printPreviewContext.imageNum = num;
};
// #endregion

// #region side by side browser
export const setSideBySideContext = (state, context) => {
  state.sideBySideContext = context;
};
export const clearSideBySideContext = state => {
  state.sideBySideContext = null;
};
export const toggleSideBySideAnnotation = (state) => {
  state.sideBySideContext.displayAnnotation = !state.sideBySideContext.displayAnnotation;
};
export const setSideBySideImageBrowser = (state, payload) => {
  if (payload.isLeft) state.sideBySideContext.leftContext = payload.context;
  else state.sideBySideContext.rightContext = payload.context;
  payload.context.startupTask();
};
export const setSideBySideImageBrowserContextImage = (state, payload) => {
  if (payload.isLeft && state.sideBySideContext.leftContext.selectImage)
    state.sideBySideContext.leftContext.selectImage(payload.image);
  else state.sideBySideContext.rightContext.selectImage(payload.image);
};
export const toggleSideBySideSync = (state) => {
  state.sideBySideContext.sync = !state.sideBySideContext.sync;
  state.sideBySideContext.leftContext.clearActiveAnnotation();
  state.sideBySideContext.leftContext.clearAnnotationMode();
  state.sideBySideContext.rightContext.clearActiveAnnotation();
  state.sideBySideContext.rightContext.clearAnnotationMode();
};
export const syncAnnotations = (state) => {
  let rightContext = state.sideBySideContext.rightContext;
  let leftContext = state.sideBySideContext.leftContext;
  rightContext.imageColorSettings = leftContext.imageColorSettings;
  rightContext.isDragLock = leftContext.isDragLock;
  rightContext.annotationColor = leftContext.annotationColor;
  let annotations = [];
  leftContext.annotations.forEach(e => {
    if (!annotations.includes(e)) {
      let annotation = deepCopyAnnotation(e);
      annotations.push(annotation);
      if (annotation.duplicateAndSyncAnnotation)
        annotation.duplicateAndSyncAnnotation(e);
    }
  });
  rightContext.annotations = annotations;
  rightContext.flattenedImageReady = leftContext.flattenedImageReady = false;
};
function recreateBaseAnnotation(original) {
  let type = original.constructor;
  let position = { ...original.originalPosition };
  let options = { ...original.options };
  return new type(position, options);
}
function deepCopyAnnotation(original) {
  return deepCopyObject(original, recreateBaseAnnotation(original));
}
function deepCopyPlainObject(original) {
  return deepCopyObject(original, {});
}
function deepCopyArray(original) {
  var copy = new Array(original.length);
  for (let i = 0; i < original.length; i++)
    deepCopyProperty(i, original, copy);
  return copy;
}
function deepCopyObject(original, copy) {
  for (const key in original)
    deepCopyProperty(key, original, copy);
  return copy;
}
function deepCopyProperty(key, original, copy) {
  let value = original[key];
  // do nothing for functions
  if (isFunction(value))
    return;

  // primitive case
  let copiedValue = value;
  if (Array.isArray(value)) {
    // handle array
    copiedValue = deepCopyArray(value);
  } else if (value instanceof Annotation) {
    // handle sub annotation
    copiedValue = deepCopyAnnotation(value);
  } else if (Object(value) === value) {
    // handle plain object
    copiedValue = deepCopyPlainObject(value);
  }
  copy[key] = copiedValue;
}
function isFunction(obj) {
  return !!(obj && obj.constructor && obj.call && obj.apply);
}
export const desyncAnnotations = (state) => {
  state.sideBySideContext.rightContext.imageColorSettings = Object.assign({}, state.sideBySideContext.leftContext.imageColorSettings);
  let rightAnnotations = state.sideBySideContext.rightContext.annotations;
  let leftAnnotations = state.sideBySideContext.leftContext.annotations;
  clearSyncState(rightAnnotations);
  clearSyncState(leftAnnotations);
};
function clearSyncState(annotations) {
  for (let i = 0; i < annotations.length; i++) {
    if ("syncPair" in annotations[i]) annotations[i].syncPair = null;
  }
}
export const syncAnnotationText = (state) => {
  if (state.textAnnotationToEdit && state.textAnnotationToEdit.syncText)
    state.textAnnotationToEdit.syncText();
};
// #endregion

// #region browser stack
export const addToBrowserStack = (state, context) => {
  state.browserStack.push(context);
};
export const popBrowserStack = (state) => {
  state.browserStack.pop();
};
export const setOpenedExams = (state, exams) => {
  state.openedExams = [...exams];
};
// #endregion

// #region help
export const openHelpDialog = state => state.showHelpDialog = true;
export const closeHelpDialog = state => state.showHelpDialog = false;
// #endregion

// #region video viewer modal
export const openVideoViewer = (state, viewer) => state.videoViewer = viewer;
export const clearVideoViewer = state => state.videoViewer = null;
// #endregion

// #region deepdee dialog
export const openDeepDeeDialog = state => state.showDeepDeeDialog = true;
export const closeDeepDeeDialog = state => state.showDeepDeeDialog = false;

// #region knowledge center settings
export const setKnowledgeCenterSettings = (state, settings) => {
  state.knowledgeCenterSettings = settings;
};
export const clearKnowledgeCenterSettings = state => state.knowledgeCenterSettings = {};

export const setVideoLibrary = (state, library) => {
  state.videoLibrary = library;
};
export const clearVideoLibrary = state => state.videoLibrary = [];

export const setKnowledgeCenterVideoSrc = (state, src) => {
  state.knowledgeCenterVideoSrc = src;
}
export const clearKnowledgeCenterVideoSrc = state => state.knowledgeCenterVideoSrc = null;

export const setBestPracticesEntries = (state, src) => {
  state.bestPracticesEntries = src;
}
export const clearBestPracticesEntries = state => state.bestPracticesEntries = [];